import type { TaskStatus } from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CircleCheckBig, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type TaskView } from '../../lib/client.ts';
import { groupByDue } from '../../lib/task-groups.ts';
import { MY_TASKS_QUERY, TaskRow, tasksQuery } from '../records/record-sidebar.tsx';
import { BlockCard } from '../records/block-card.tsx';
import { TaskGroup } from '../tasks/task-group.tsx';
import { EmptyState } from '../ui/empty-state.tsx';
import { useNow } from './time.ts';

/**
 * Home's Tasks: my open manual tasks that are Overdue or due Today (My Tasks' grouping, in my time
 * zone). A task can be marked done here, as on My Tasks (it then leaves the list); anything else
 * opens on its record, and My Tasks is one click away.
 */
export function TasksCard({ fill = false }: { fill?: boolean }) {
  const tasks = useQuery({
    // Under My Tasks' key, so a change made there or on a record refreshes this too.
    // Manual tasks only: automated ones (sent by pipeline triggers) are not the user's to do.
    queryKey: [...MY_TASKS_QUERY, 'open', undefined, false],
    queryFn: () => api.tasks.mine('open', undefined, false),
  });
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // As My Tasks: all of the user's task lists and the record's own refresh after the change.
  const setStatus = useMutation({
    mutationFn: ({ task, status }: { task: TaskView; status: TaskStatus }) =>
      api.tasks.patch(task.id, { status }),
    onSuccess: async (t) => {
      setError(null);
      await qc.invalidateQueries({ queryKey: MY_TASKS_QUERY });
      await qc.invalidateQueries({ queryKey: tasksQuery(t.entityId, t.entityType) });
    },
    onError: (err) => setError(describeError(err)),
  });
  // Re-grouped every minute, so a tab left open moves tasks to Overdue as they pass due.
  const now = useNow();
  const groups = groupByDue(tasks.data ?? [], now).filter(
    (g) => (g.name === 'Overdue' || g.name === 'Today') && g.items.length > 0,
  );
  return (
    <BlockCard
      title="Tasks"
      count={tasks.data ? groups.reduce((n, g) => n + g.items.length, 0) : undefined}
      fill={fill}
      action={
        <Link to="/tasks" className="text-primary text-xs leading-7 font-medium hover:underline">
          View all
        </Link>
      }
    >
      {() =>
        tasks.isPending ? (
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden /> Loading…
          </p>
        ) : tasks.error ? (
          <p className="text-danger text-sm" role="alert">
            {describeError(tasks.error)}
          </p>
        ) : groups.length === 0 ? (
          <EmptyState
            icon={CircleCheckBig}
            title="Nothing overdue or due today"
            description="Tasks assigned to you show here on the day they are due."
            data-testid="home-no-tasks"
            className="h-full"
          />
        ) : (
          <div className="flex flex-col gap-3">
            {error && (
              <p className="text-danger text-sm" role="alert">
                {error}
              </p>
            )}
            {groups.map((g) => (
              <TaskGroup key={g.name} name={g.name} count={g.items.length}>
                {g.items.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    onStatusChange={(status) => setStatus.mutate({ task: t, status })}
                    // Assigned to the user, so theirs to close (as on My Tasks).
                    canToggle
                    canDelete={false}
                    showRecord
                  />
                ))}
              </TaskGroup>
            ))}
          </div>
        )
      }
    </BlockCard>
  );
}
